import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Ticket, CheckCircle, Clock } from 'lucide-react';
import { api } from '@/services/api';
import { useStore } from '@/store/useStore';
import type { TicketImage, UserTicket } from '@/types';
import { toast } from '@/store/useToast';

const API_BASE = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:8000';

export const TicketsShopPage = () => {
  const navigate = useNavigate();
  const { user, fetchUser } = useStore();

  const [availableTickets, setAvailableTickets] = useState<TicketImage[]>([]);
  const [myTickets, setMyTickets] = useState<UserTicket[]>([]);
  const [tab, setTab] = useState<'shop' | 'my'>('shop');
  const [buying, setBuying] = useState<number | null>(null);
  const [loadingShop, setLoadingShop] = useState(true);
  const [loadingMy, setLoadingMy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadShop();
    loadMyTickets();
  }, []);

  const loadShop = async () => {
    try {
      setLoadingShop(true);
      const tickets = await api.getAvailableTickets();
      setAvailableTickets(tickets);
    } catch {
      setError('Не удалось загрузить билеты');
    } finally {
      setLoadingShop(false);
    }
  };

  const loadMyTickets = async () => {
    try {
      setLoadingMy(true);
      const tickets = await api.getMyTickets();
      setMyTickets(tickets);
    } catch {
      // silent — user may not be logged in yet
    } finally {
      setLoadingMy(false);
    }
  };

  const handleBuy = async (ticketImage: TicketImage) => {
    if (!user) return;
    if (user.balance < ticketImage.price) {
      toast.error(`Недостаточно средств. Нужно ${ticketImage.price}₽, у вас ${user.balance.toFixed(0)}₽`);
      return;
    }

    setBuying(ticketImage.id);
    setError(null);
    try {
      await api.buyTicket({ ticket_image_id: ticketImage.id });
      await fetchUser();
      await loadMyTickets();
      toast.success(`Билет «${ticketImage.name}» куплен!`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Ошибка при покупке';
      setError(detail);
    } finally {
      setBuying(null);
    }
  };

  const unusedCount = myTickets.filter((t) => !t.is_used).length;

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 z-10">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <ShoppingBag size={22} />
          Магазин билетов
        </h1>
        {user && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Баланс: <span className="font-semibold text-gray-800 dark:text-gray-200">{user.balance.toFixed(0)}₽</span>
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <button
          onClick={() => setTab('shop')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            tab === 'shop'
              ? 'text-telegram-button border-b-2 border-telegram-button'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          Купить билет
        </button>
        <button
          onClick={() => setTab('my')}
          className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
            tab === 'my'
              ? 'text-telegram-button border-b-2 border-telegram-button'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          Мои билеты
          {unusedCount > 0 && (
            <span className="absolute top-2 right-6 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unusedCount}
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Shop tab */}
      {tab === 'shop' && (
        <div className="px-4 py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Купите билет — и используйте его для участия в розыгрыше с соответствующей ценой входа.
            Один билет = одно участие.
          </p>

          {loadingShop ? (
            <div className="text-center py-10 text-gray-500">Загрузка...</div>
          ) : availableTickets.length === 0 ? (
            <div className="text-center py-10 text-gray-500">Нет доступных билетов</div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {availableTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden flex"
                >
                  {/* Image */}
                  <div className="w-32 h-32 flex-shrink-0 bg-gray-100 dark:bg-gray-700">
                    <img
                      src={`${API_BASE}/ticket-images/${ticket.filename}`}
                      alt={ticket.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-4 flex flex-col justify-between">
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-base">
                        {ticket.name}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Для розыгрышей с ценой входа {ticket.price.toFixed(0)}₽
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xl font-bold text-telegram-button">
                        {ticket.price.toFixed(0)}₽
                      </span>
                      <button
                        onClick={() => handleBuy(ticket)}
                        disabled={buying === ticket.id || !user}
                        className="px-4 py-2 bg-telegram-button text-telegram-buttonText rounded-xl text-sm font-semibold disabled:opacity-50 transition-opacity"
                      >
                        {buying === ticket.id ? 'Покупка...' : 'Купить'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My tickets tab */}
      {tab === 'my' && (
        <div className="px-4 py-4">
          {loadingMy ? (
            <div className="text-center py-10 text-gray-500">Загрузка...</div>
          ) : myTickets.length === 0 ? (
            <div className="text-center py-10">
              <Ticket size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">У вас нет билетов</p>
              <button
                onClick={() => setTab('shop')}
                className="mt-4 px-6 py-2 bg-telegram-button text-telegram-buttonText rounded-xl text-sm font-semibold"
              >
                Купить билет
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {myTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`bg-white dark:bg-gray-800 rounded-2xl shadow overflow-hidden flex ${
                    ticket.is_used ? 'opacity-50' : ''
                  }`}
                >
                  {/* Image */}
                  <div className="w-20 h-20 flex-shrink-0 bg-gray-100 dark:bg-gray-700 relative">
                    <img
                      src={`${API_BASE}/ticket-images/${ticket.image_filename}`}
                      alt={ticket.image_name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    {ticket.is_used && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <CheckCircle size={24} className="text-white" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 p-3 flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-gray-900 dark:text-white text-sm">
                        {ticket.image_name}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          ticket.is_used
                            ? 'bg-gray-100 text-gray-500 dark:bg-gray-700'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                        }`}
                      >
                        {ticket.is_used ? 'Использован' : 'Активен'}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm text-gray-500">
                        {ticket.price_paid.toFixed(0)}₽
                      </span>
                      {!ticket.is_used && (
                        <button
                          onClick={() => navigate('/raffles')}
                          className="text-xs px-3 py-1 bg-telegram-button text-telegram-buttonText rounded-lg font-medium"
                        >
                          Использовать
                        </button>
                      )}
                      {ticket.is_used && ticket.raffle_id && (
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock size={12} />
                          Розыгрыш #{ticket.raffle_id}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
